import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { CircularProgress, CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { EventType, IPublicClientApplication } from '@azure/msal-browser';
import { EN_LANG_CODE, LOCALIZATION_GROUPS, PARTICIPANT_TOKEN_PARAM, ROUTES } from '@constants';
import Header from '@features/Header';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import ParticipantFinishScreen from '@features/ParticipantFinishScreen';
import { lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Nullable } from '@customTypes';
import { setParticipantMode, setParticipantToken } from '@store/reducers/user.reducer';
import { useGetTranslationsQuery } from '@services/api/translation.api';
import { getLanguageCode } from '@utils/helpers';
import { resetSelectedInterview } from '@store/reducers/interview.reducer';

const Survey = lazy(() => import('@features/Survey'));
const SelectInterviewPage = lazy(() => import('@features/SelectInterviewPage'));
const Homepage = lazy(() => import('@features/Homepage'));
const Project = lazy(() => import('@features/Project'));

import { getTheme } from './App.styles';
import './App.scss';

interface AppProps {
  instance: Nullable<IPublicClientApplication>;
}

const App = ({ instance }: AppProps) => {
  const { i18n } = useTranslation();
  const [params] = useSearchParams();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { theme, isParticipantMode, language } = useAppSelector((state) => state.user);
  const { isInterviewCompleted, selectedInterviewData } = useAppSelector((state) => state.interview);

  const { isLoading } = useGetTranslationsQuery({
    langCode:
      (location.pathname === ROUTES.SURVEY ? selectedInterviewData?.langCode : getLanguageCode(language)) ||
      EN_LANG_CODE,
    localizationGroup: LOCALIZATION_GROUPS.ALL,
  });

  useEffect(() => {
    const callbackId = instance?.addEventCallback((message: { eventType: EventType }) => {
      if (message.eventType === EventType.LOGIN_SUCCESS) {
        sessionStorage.removeItem('persist:root');
      }
    });

    return () => {
      if (callbackId) instance?.removeEventCallback(callbackId);
    };
  }, []);

  useEffect(() => {
    const participantToken = params.get(PARTICIPANT_TOKEN_PARAM);

    dispatch(setParticipantToken(participantToken));
    dispatch(setParticipantMode(!!participantToken));
  }, [params]);

  useEffect(() => {
    if (isParticipantMode) {
      dispatch(resetSelectedInterview());
    }
  }, [isParticipantMode]);

  useEffect(() => void i18n.changeLanguage(language), [language]);

  const muiTheme = getTheme(theme);

  if (isLoading && !isParticipantMode) return <CircularProgress />;

  return (
    <ThemeProvider theme={muiTheme}>
      <StyledEngineProvider injectFirst>
        <AuthenticatedTemplate>
          <CssBaseline enableColorScheme />
          <Header>
            <Routes>
              <Route index element={<Homepage />} />
              <Route path={ROUTES.SELECT_INTERVIEW} element={<SelectInterviewPage />} />
              <Route path={ROUTES.SURVEY} element={<Survey />} />
              <Route path={ROUTES.PROJECT} element={<Project />} />
            </Routes>
          </Header>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          {isParticipantMode && (
            <>
              <CssBaseline enableColorScheme />
              {isInterviewCompleted ? (
                <ParticipantFinishScreen />
              ) : (
                <Header>
                  <Routes>
                    <Route index element={<Survey />} />
                  </Routes>
                </Header>
              )}
            </>
          )}
        </UnauthenticatedTemplate>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default App;
